import styled from '@emotion/styled'
import { ClearingHouse } from 'api'
import { Locale, Translation } from 'locale'
import React, { useCallback, useState } from 'react'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { DialogRow, DialogField } from 'view/components/inputs'
import { ModalWindow, ModalFooter, Groupbox } from 'view/components/panels'
import { Tabs } from 'view/components/tabs'
import { CollateralBasketsPanel } from './CollateralBasketsPanel'
import { CollateralsPanel } from './CollateralsPanel'
import { DetailsPanel } from './DetailsPanel'

interface ClearingHouseDialogComponentProps {
  title: string
  clearingHouse: ClearingHouse
  hasValidationIssues: boolean
  readOnly: boolean
  onUpdate: (clearingHouse: Partial<ClearingHouse>) => void
  onClose: () => void
  onSave: () => void
}

const Content = styled(Groupbox)`
  width: 810px;
  padding: 20px 20px 0 20px;
  min-height: 450px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;

  & > *:last-child {
    border-bottom: none;
  }
`

export const ClearingHouseDialogComponent: React.FC<ClearingHouseDialogComponentProps> = ({
  title,
  clearingHouse,
  hasValidationIssues,
  readOnly,
  onUpdate,
  onClose,
  onSave
}) => {
  const [selectedTab, setSelectedTab] = useState(Translation.TC_CLEARING_HOUSE_DETAILS)
  const onChangeTab = useCallback(
    (tab: Translation) => {
      setSelectedTab(tab)
    },
    [setSelectedTab]
  )
  return (
    <ModalWindow title={title}>
      <Tabs
        selectedTab={selectedTab}
        tabs={[Translation.TC_CLEARING_HOUSE_DETAILS, Translation.TC_SECTION_COLLATERALS, Translation.TC_SECTION_COLLATERAL_BASKETS]}
        onChangeTab={onChangeTab}
      />
      <Content>
        {selectedTab === Translation.TC_CLEARING_HOUSE_DETAILS && (
          <DetailsPanel clearingHouse={clearingHouse} readOnly={readOnly} onUpdate={onUpdate} />
        )}
        {selectedTab === Translation.TC_SECTION_COLLATERALS && (
          <CollateralsPanel clearingHouse={clearingHouse} readOnly={readOnly} onUpdate={onUpdate} />
        )}
        {selectedTab === Translation.TC_SECTION_COLLATERAL_BASKETS && (
          <CollateralBasketsPanel clearingHouse={clearingHouse} readOnly={readOnly} onUpdate={onUpdate} />
        )}
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'ClearingHouseDialog-TextButton-ButtonCancel'} type={TextButtonType.Tertiary} onClick={onClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'ClearingHouseDialog-TextButton-ButtonSave'}
              type={TextButtonType.Primary}
              disabled={readOnly || hasValidationIssues}
              onClick={onSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
